import type { TeamMemberWithUserDto } from '@up2stay/up2promo-api-client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  brandAvatarGlowClass,
  brandGradFillClass,
  cn,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { UserMinus, UserPlus } from 'lucide-react';

import {
  SettingsGroup,
  SettingsGroupLabel,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';

const TEAM_MEMBERS_SCROLL_HIDE_CLASS =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join('');
}

function displayName(member: TeamMemberWithUserDto): string {
  const u = member.user;
  if (u?.firstName && u?.lastName) return `${u.firstName} ${u.lastName}`;
  if (u?.firstName) return u.firstName;
  if (u?.email) return u.email;
  return member.userId;
}

export interface TeamMembersCardProps {
  members: TeamMemberWithUserDto[];
  memberCount?: number;
  isLoading: boolean;
  canManage: boolean;
  scrollable: boolean;
  onAddMember: () => void;
  onRemoveMember: (member: TeamMemberWithUserDto) => void;
  dict: {
    membersTitle: string;
    noMembers: string;
    addMember: string;
  };
}

export function TeamMembersCard({
  members,
  memberCount,
  isLoading,
  canManage,
  scrollable,
  onAddMember,
  onRemoveMember,
  dict,
}: TeamMembersCardProps) {
  return (
    <section
      id="team-members"
      className={cn(
        'flex scroll-mt-4 flex-col gap-2',
        scrollable && 'h-full min-h-0 w-full max-h-full overflow-hidden',
      )}
      aria-labelledby="team-members-title"
    >
      <div className="flex shrink-0 items-start justify-between gap-3">
        <SettingsGroupLabel id="team-members-title">
          {dict.membersTitle}
        </SettingsGroupLabel>
        <div className="flex shrink-0 items-center gap-2">
          {memberCount != null ? (
            <Badge
              variant="outline"
              className="font-normal text-muted-foreground"
            >
              {memberCount}
            </Badge>
          ) : null}
          {canManage ? (
            <Button
              size="sm"
              className="min-h-9 shrink-0"
              onClick={onAddMember}
            >
              <UserPlus className="mr-1 h-4 w-4" />
              {dict.addMember}
            </Button>
          ) : null}
        </div>
      </div>

      <SettingsGroup
        className={cn(
          scrollable && 'flex min-h-0 flex-1 flex-col overflow-hidden',
        )}
      >
        <div
          className={cn(
            scrollable &&
              `min-h-0 flex-1 overflow-y-auto overscroll-y-contain ${TEAM_MEMBERS_SCROLL_HIDE_CLASS}`,
          )}
        >
          {isLoading ? (
            <SettingsGroupRows>
              {Array.from({ length: 3 }).map((_, i) => (
                <SettingsGroupRow key={i} separated={i > 0}>
                  <Skeleton className="h-10 w-full" />
                </SettingsGroupRow>
              ))}
            </SettingsGroupRows>
          ) : members.length === 0 ? (
            <SettingsGroupRows>
              <SettingsGroupRow>
                <p className="py-6 text-center text-[13.5px] leading-relaxed text-muted-foreground">
                  {dict.noMembers}
                </p>
              </SettingsGroupRow>
            </SettingsGroupRows>
          ) : (
            <SettingsGroupRows>
              {members.map((member, index) => {
                const name = displayName(member);
                const initials = getInitials(name);
                return (
                  <SettingsGroupRow key={member.id} separated={index > 0}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar className={cn('size-9', brandAvatarGlowClass)}>
                          {member.user?.photo?.path ? (
                            <AvatarImage src={member.user.photo.path} />
                          ) : null}
                          <AvatarFallback
                            className={cn(brandGradFillClass, 'text-[11px]')}
                          >
                            {initials || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-[13.5px] font-medium leading-relaxed text-foreground">
                            {name}
                          </p>
                          {member.user?.email ? (
                            <p className="truncate text-[11px] leading-relaxed text-muted-foreground">
                              {member.user.email}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {canManage ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveMember(member)}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </SettingsGroupRow>
                );
              })}
            </SettingsGroupRows>
          )}
        </div>
      </SettingsGroup>
    </section>
  );
}
